import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  IconButton,
  Typography,
  List,
  ListItem,
  ListItemText,
  Avatar,
  Alert,
  Button,
  Divider,
  InputAdornment,
  Collapse,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Chip,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { chatService } from '../services/api';

const ChatSidebar = ({ onNewChat, onSelectChat, selectedChatId }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [expandedSections, setExpandedSections] = useState({ today: true });
  const [savedChats, setSavedChats] = useState([]);

  useEffect(() => {
    loadSavedChats();
  }, []);

  const loadSavedChats = async () => {
    try {
      const chats = await chatService.getChatHistory();
      setSavedChats(chats);
    } catch (error) {
      console.error('Error loading saved chats:', error);
    }
  };

  const groupChatsByDate = (chats) => {
    const today = new Date().toDateString();
    const yesterday = new Date(Date.now() - 86400000).toDateString();

    return chats.reduce((acc, chat) => {
      const chatDate = new Date(chat.created_at).toDateString();
      let period;

      if (chatDate === today) {
        period = 'today';
      } else if (chatDate === yesterday) {
        period = 'yesterday';
      } else {
        const month = new Date(chat.created_at)
          .toLocaleString('default', { month: 'long' })
          .toLowerCase();
        period = month;
      }

      if (!acc[period]) {
        acc[period] = [];
      }
      acc[period].push(chat);
      return acc;
    }, {});
  };

  const filteredChatHistory = Object.entries(groupChatsByDate(savedChats)).reduce(
    (acc, [period, chats]) => {
      const filteredChats = chats.filter((chat) =>
        chat.title.toLowerCase().includes(searchQuery.toLowerCase())
      );
      if (filteredChats.length > 0) {
        acc[period] = filteredChats;
      }
      return acc;
    },
    {}
  );

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <Paper
      elevation={0}
      sx={{
        width: 280,
        height: '100%',
        borderRight: '1px solid rgba(0, 0, 0, 0.12)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ flexGrow: 1 }}>
            Chats
          </Typography>
          <IconButton size="small" onClick={() => setShowSearch(!showSearch)}>
            <SearchIcon />
          </IconButton>
        </Box>
        <Collapse in={showSearch}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search chats..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </Collapse>
        <Button
          fullWidth
          variant="contained"
          startIcon={<AddIcon />}
          onClick={onNewChat}
          sx={{
            backgroundColor: '#BF2924',
            '&:hover': {
              backgroundColor: '#a61f1b',
            },
            textTransform: 'none',
          }}
        >
          New Chat
        </Button>
      </Box>

      <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
        {Object.entries(filteredChatHistory).map(([period, chats]) => (
          <Box key={period}>
            <ListItem button onClick={() => toggleSection(period)} sx={{ py: 1 }}>
              <ListItemText
                primary={period.charAt(0).toUpperCase() + period.slice(1)}
                primaryTypographyProps={{
                  variant: 'body2',
                  color: 'text.secondary',
                }}
              />
              {chats.length > 0 &&
                (expandedSections[period] ? (
                  <KeyboardArrowDownIcon fontSize="small" color="action" />
                ) : (
                  <KeyboardArrowRightIcon fontSize="small" color="action" />
                ))}
            </ListItem>
            <Collapse in={expandedSections[period]}>
              {chats.map((chat) => (
                <ListItem
                  key={chat.id}
                  button
                  selected={selectedChatId === chat.id}
                  onClick={() => onSelectChat(chat)}
                  sx={{
                    pl: 3,
                    py: 0.5,
                    '&:hover': {
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    },
                    '&.Mui-selected': {
                      backgroundColor: 'rgba(191, 41, 36, 0.08)',
                      '&:hover': {
                        backgroundColor: 'rgba(191, 41, 36, 0.12)',
                      },
                    },
                  }}
                >
                  <ListItemText
                    primary={chat.title}
                    primaryTypographyProps={{
                      variant: 'body2',
                      noWrap: true,
                    }}
                  />
                </ListItem>
              ))}
            </Collapse>
          </Box>
        ))}
      </Box>
    </Paper>
  );
};

const WelcomeMessage = () => (
  <Box
    sx={{
      textAlign: 'center',
      maxWidth: 800,
      mx: 'auto',
      mt: 8,
      mb: 4,
      px: 2,
    }}
  >
    <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600 }}>
      Arcogni AI Chat
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
    Welcome to Arcogni, a pioneering autonomous AI platform designed to accelerate scientific and engineering advancements. 
    Arcogni leverages artificial intelligence to enhance data analysis, enabling the efficient aggregation of small evolutionary discoveries into generational breakthroughs 
    that transform innovation across disciplines. Our capabilities include Artificial Intelligence as a Service (AIAAS), 
    providing software that uses AI to generate algorithms for research and development in data analysis, as well as Platform as a Service (PAAS) 
    solutions featuring computer software platforms specifically built to support algorithm generation for research and development in data analysis.
    </Typography>
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        gap: 4,
        mt: 4,
      }}
    >
      {[
        { title: 'Natural Conversation', icon: '🗣️' },
        { title: 'Knowledge Base', icon: '📚' },
        { title: 'Seamless Integration', icon: '🔄' },
      ].map((feature) => (
        <Box
          key={feature.title}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="h6">{feature.icon}</Typography>
          <Typography variant="body2" color="text.secondary">
            {feature.title}
          </Typography>
        </Box>
      ))}
    </Box>
  </Box>
);

const Chat = () => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedChat, setSelectedChat] = useState(null);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadedFileName, setUploadedFileName] = useState('');

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      setSelectedFile(file);
      setUploadedFileName(file.name);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
  
    try {
      // Add user message to chat
      setMessages((prev) => [...prev, { text: input, sender: 'user', timestamp: new Date().toISOString() }]);

      // Send message to backend with file if selected
      const response = await chatService.sendMessage(input, selectedFile);
      
      // Clear file after sending
      setSelectedFile(null);
      setUploadedFileName('');

      console.log('Backend response:', response); // Debug log

      // Create a comprehensive message object based on response
      const botMessage = {
        sender: 'bot',
        timestamp: new Date().toISOString(),
      };

      // PRIORITY 1: Handle CNN/NN/Algorithm responses (field_n3, field_n4, field_n5, field_n6)
      if (response.algorithm) {
        botMessage.algorithm = response.algorithm;
        // Get text from pred or message
        botMessage.text = response.pred || response.message || "Here's a new architecture for your data.";
        // Get details from comp or details
        botMessage.details = response.comp || response.details || "";
      }
      
      if (response.graph) {
        botMessage.graph = response.graph;
      }
      
      // PRIORITY 2: Handle Polymer/Chemical Structure responses (field_n2)
      if (response.polymer) {
        botMessage.polymer = response.polymer;
        botMessage.text = botMessage.text || response.pred || response.message || "";
        botMessage.details = botMessage.details || response.comp || response.details || "";
      }
      
      if (response.chem_str) {
        botMessage.structure = response.chem_str;
      }
      
      if (response.graph_FI) {
        botMessage.visualization = response.graph_FI;
      }
      
      if (response.pred_polymer) {
        botMessage.predictions = response.pred_polymer;
      }
      
      // PRIORITY 3: Handle legacy/generic responses
      if (!botMessage.text) {
        if (response.pred) {
          botMessage.text = response.pred;
        } else if (response.message) {
          botMessage.text = Array.isArray(response.message) 
            ? response.message.join(' ') 
            : response.message;
        }
      }
      
      if (!botMessage.details && response.comp) {
        botMessage.details = response.comp;
      }
      
      if (response.texts && Array.isArray(response.texts)) {
        botMessage.texts = response.texts;
        if (!botMessage.text) botMessage.text = response.texts[0];
        if (!botMessage.details && response.texts[1]) botMessage.details = response.texts[1];
      }
      
      // Legacy success format support
      if (response.success) {
        if (!botMessage.text && response.message) botMessage.text = response.message;
        if (!botMessage.details && response.details) botMessage.details = response.details;
        if (!botMessage.structure && response.structure) botMessage.structure = response.structure;
        if (!botMessage.visualization && response.visualization) botMessage.visualization = response.visualization;
        if (!botMessage.predictions && response.predictions) botMessage.predictions = response.predictions;
      }
      
      console.log('Processed bot message:', botMessage); // Debug log
      
      setMessages((prev) => [...prev, botMessage]);
      setInput('');
      
    } catch (error) {
      console.error('Error sending message:', error);
      setMessages((prev) => [
        ...prev,
        {
          text: 'Sorry, there was an error processing your request.',
          sender: 'bot',
          error: true,
          timestamp: new Date().toISOString(),
        },
      ]);
    }
  };

  const handleNewChat = () => {
    setMessages([]);
    setInput('');
    setError('');
    setSelectedChat(null);
    setCurrentSessionId(null);
  };


  function renderMessageWithCode(message) {
    // Split message by triple backticks ```
    // Odd indexes = code blocks, even = normal text
    const parts = message.split(/```/);
    
    return parts.map((part, i) => {
      if (i % 2 === 1) {
        // This is a code block
        return (
          <Box
            key={i}
            component="pre"
            sx={{
              backgroundColor: '#282c34',
              color: '#f8f8f2',
              padding: 2,
              borderRadius: 2,
              fontFamily: 'Source Code Pro, monospace, monospace',
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              mt: 2,
            }}
          >
            {part.trim()}
          </Box>
        );
      } else {
        // Normal text chunk, render with Typography
        return (
          <Typography
            key={i}
            variant="body1"
            component="span"
            style={{ whiteSpace: 'pre-wrap' }} // preserve new lines
          >
            {part}
          </Typography>
        );
      }
    });
  }

  const handleSelectChat = (chat) => {
    setSelectedChat(chat);
    setMessages(
      chat.messages.map((msg) => ({
        text: msg.content,
        details: msg.details || null,
        polymer: msg.polymer || null,
        visualization: msg.visualization || null,
        structure: msg.structure || null,
        predictions: msg.predictions || null,
        algorithm: msg.algorithm || null,
        graph: msg.graph || null,
        sender: msg.role === 'user' ? 'user' : 'bot',
        timestamp: msg.timestamp,
      }))
    );
    setError('');
    setCurrentSessionId(chat.id);
  };
  
  return (
    <Box
      sx={{
        height: 'calc(100vh - 64px)', // Subtract navbar height
        display: 'flex',
      }}
    >
      <ChatSidebar
        onNewChat={handleNewChat}
        onSelectChat={handleSelectChat}
        selectedChatId={selectedChat?.id}
      />
  
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          position: 'relative',
        }}
      >
        <Box
          sx={{
            flexGrow: 1,
            overflow: 'auto',
            p: 2,
            backgroundColor: '#f5f5f5',
          }}
        >
          {messages.length === 0 ? (
            <WelcomeMessage />
          ) : (
            <List>
              {messages.map((message, index) => (
                <ListItem
                  key={index}
                  sx={{
                    flexDirection: message.sender === 'user' ? 'row-reverse' : 'row',
                    gap: 1,
                    mb: 1,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: message.sender === 'user' ? 'primary.main' : 'secondary.main',
                    }}
                  >
                    {message.sender === 'user' ? 'U' : 'A'}
                  </Avatar>
                  <Paper
                    sx={{
                      p: 2,
                      maxWidth: message.sender === 'bot' && (message.visualization || message.structure || message.algorithm || message.graph) ? '90%' : '70%',
                      backgroundColor: message.sender === 'user' ? 'primary.light' : 'white',
                      color: message.sender === 'user' ? 'white' : 'text.primary',
                    }}
                  >
                    {message.sender === 'user' ? (
                      <ListItemText
                        primary={message.text}
                        secondary={message.timestamp ? new Date(message.timestamp).toLocaleTimeString() : ''}
                        secondaryTypographyProps={{
                          color: 'white',
                        }}
                      />
                    ) : (
                      <Box>
                        {/* Main text message */}
                        {(message.text || (message.texts && message.texts[0])) && (
                          <Typography variant="body1" gutterBottom>
                            {message.text || message.texts[0]}
                          </Typography>
                        )}
                        
                        {/* Details/comparison text */}
                        {(message.details || (message.texts && message.texts[1])) && (
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                            {message.details || message.texts[1]}
                          </Typography>
                        )}
                        
                        {/* Algorithm code - PLACED BEFORE GRAPH */}
                        {message.algorithm && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Generated Algorithm:
                            </Typography>
                            <Paper sx={{ p: 2, bgcolor: 'grey.900', color: 'white', overflow: 'auto', maxHeight: 400 }}>
                              <Typography
                                variant="body2"
                                component="pre"
                                sx={{ 
                                  fontFamily: 'monospace', 
                                  fontSize: '0.85em',
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                  margin: 0
                                }}
                              >
                                {message.algorithm}
                              </Typography>
                            </Paper>
                          </Box>
                        )}
                        
                        {/* General graph (for neural architecture, etc.) - PLACED AFTER ALGORITHM */}
                        {message.graph && (
                          <Card sx={{ mb: 2 }}>
                            <CardContent>
                              <Typography variant="subtitle2" gutterBottom>
                                Performance Comparison
                              </Typography>
                              <CardMedia
                                component="img"
                                image={message.graph}
                                alt="Performance Graph"
                                sx={{ maxWidth: '100%', height: 'auto' }}
                              />
                            </CardContent>
                          </Card>
                        )}
                        
                        {/* Chemical structure image */}
                        {message.structure && (
                          <Card sx={{ mb: 2 }}>
                            <CardContent>
                              <Typography variant="subtitle2" gutterBottom>
                                Chemical Structure
                              </Typography>
                              <CardMedia
                                component="img"
                                image={message.structure}
                                alt="Chemical Structure"
                                sx={{ maxHeight: 300, objectFit: 'contain' }}
                              />
                            </CardContent>
                          </Card>
                        )}
                        
                        {/* Polymer SMILES */}
                        {message.polymer && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              SMILES Notation:
                            </Typography>
                            <Paper sx={{ p: 1, bgcolor: 'grey.100' }}>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {message.polymer}
                              </Typography>
                            </Paper>
                          </Box>
                        )}
                        
                        {/* Predictions table */}
                        {message.predictions && Array.isArray(message.predictions) && (
                          <Box sx={{ mb: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Predictions:
                            </Typography>
                            <TableContainer component={Paper}>
                              <Table size="small">
                                <TableBody>
                                  {message.predictions.map((pred, idx) => (
                                    <TableRow key={idx}>
                                      <TableCell>{pred[0]}</TableCell>
                                      <TableCell align="right">
                                        <Chip label={pred[1]} color="primary" size="small" />
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          </Box>
                        )}
                        
                        {/* Visualization graph */}
                        {message.visualization && (
                          <Card sx={{ mb: 2 }}>
                            <CardContent>
                              <Typography variant="subtitle2" gutterBottom>
                                Visualization
                              </Typography>
                              <CardMedia
                                component="img"
                                image={message.visualization}
                                alt="Visualization"
                                sx={{ maxWidth: '100%', height: 'auto' }}
                              />
                            </CardContent>
                          </Card>
                        )}
                        
                        {/* Timestamp */}
                        <Typography variant="caption" color="text.secondary">
                          {message.timestamp ? new Date(message.timestamp).toLocaleTimeString() : ''}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </ListItem>
              ))}
            </List>
          )}
        </Box>
  
        {error && (
          <Alert
            severity="error"
            sx={{
              position: 'absolute',
              bottom: '80px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 1,
            }}
          >
            {error}
          </Alert>
        )}
  
        <Box
          component="form"
          onSubmit={handleSendMessage}
          sx={{
            p: 2,
            backgroundColor: 'background.paper',
            borderTop: '1px solid rgba(0, 0, 0, 0.12)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              maxWidth: 800,
              mx: 'auto',
            }}
          >
            <input
              type="file"
              id="file-upload"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
              accept=".pkl,.npz,.txt,.csv,.json"
            />
            <label htmlFor="file-upload">
              <IconButton
                component="span"
                sx={{
                  bgcolor: selectedFile ? '#4caf50' : '#f5f5f5',
                  color: selectedFile ? 'white' : 'text.secondary',
                  '&:hover': {
                    bgcolor: selectedFile ? '#45a049' : '#e0e0e0',
                  },
                }}
              >
                <AttachFileIcon />
              </IconButton>
            </label>
            <TextField
              fullWidth
              variant="outlined"
              placeholder={loading ? 'Waiting for response...' : uploadedFileName || 'Write your message here...'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              disabled={loading}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            />
            <IconButton
              type="submit"
              color="primary"
              disabled={!input.trim() || loading}
              sx={{
                bgcolor: '#BF2924',
                color: 'white',
                '&:hover': {
                  bgcolor: '#a61f1b',
                },
                '&.Mui-disabled': {
                  bgcolor: 'rgba(191, 41, 36, 0.5)',
                  color: 'white',
                },
              }}
            >
              <SendIcon />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
  
};

export default Chat;
